import { setAuthenticator } from '@ms/yammer-data/dist/authentication';
import { reportInfo } from '@ms/yammer-data/dist/telemetry';
import { Authenticator, AuthenticatorType, VivaConnectionsAuthenticator } from '@ms/yammer-web-authenticators';
import { TeamsAuthenticator } from '@ms/yammer-web-authenticators/dist/teams';
import { getNetworkContext } from '@ms/yammer-web-support/dist/location';
import { getMetaOsHubContextThrows, isInMetaOsHub } from '@ms/yammer-web-support/dist/metaOsHub';
import { isMobileEmbedView } from '@ms/yammer-web-support/dist/userAgent';
import config from 'bootstrap/config';
import { ensureVivaConnectionsInitialized, getAuthToken } from 'mobileEmbedView';

import { getAuthTokenCache, initializeAuthTokenCache } from './authTokenCache';
import { isNAASupportAvailable } from './isNAASupportAvailable';
import { reportAuthError } from './reportAuthError';

type GetAuthenticator = () => Promise<Authenticator>;
const getAuthenticator: GetAuthenticator = async () => {
  if (await isInMetaOsHub()) {
    initializeAuthTokenCache();

    if (await isNAASupportAvailable()) {
      const teamsAuthenticatorV2Promise = import(
        /* webpackChunkName: "4-auth-naa" */ '@ms/yammer-web-authenticators/dist/teams'
      );

      reportAuthenticatorType(AuthenticatorType.NestedAppAuth);
      const context = await getMetaOsHubContextThrows();
      const tenant = config.adal.tenant;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (config as any).adal = config.msal;

      const authenticatorOptions = {
        getAuthTokenCache,
        instance: config.adal.instance,
        clientId: config.adal.clientId,
        tenant,
        onError: reportAuthError,
        accountFilter: {
          homeAccountId: context.user?.id,
          tenantId: context.user?.tenant?.id,
          loginHint: context.user?.loginHint,
        },
      };

      const { TeamsAuthenticatorV2 } = await teamsAuthenticatorV2Promise;

      return new TeamsAuthenticatorV2(authenticatorOptions);
    }
    reportAuthenticatorType(AuthenticatorType.Teams);

    return new TeamsAuthenticator(getAuthTokenCache);
  }

  if (isMobileEmbedView()) {
    ensureVivaConnectionsInitialized();
    reportAuthenticatorType(AuthenticatorType.VivaConnections);

    return new VivaConnectionsAuthenticator(getAuthToken);
  }

  const networkContext = getNetworkContext(window.location.pathname);
  const tenant = networkContext?.networkType === 'Canonical' ? networkContext.permalink : config.msal.tenant;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (config as any).adal = config.msal;

  const { default: MsalRedirectAuthenticator } = await import(
    /* webpackChunkName: "4-auth-msal" */ '@ms/yammer-web-authenticators/dist/redirect/MsalRedirectAuthenticator'
  );
  reportAuthenticatorType(AuthenticatorType.MsalRedirect);

  return new MsalRedirectAuthenticator({
    ...config.msal,
    tenant,
  });
};

type SetupAuthenticator = () => Promise<Authenticator>;
export const setupAuthenticator: SetupAuthenticator = async () => {
  const authenticator = await getAuthenticator();
  setAuthenticator(authenticator);

  return authenticator;
};

export const reportAuthenticatorType = (authenticatorType: AuthenticatorType) => {
  reportInfo({
    eventName: 'teams_authenticator_info',
    eventProperties: {
      authenticatorType,
    },
  });
};
